import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { AvisoError, useAccion } from "./comun.tsx";

interface Evento { id: number | string; creado: string; tipo: string; severidad: "info" | "media" | "alta"; clinica: string; email: string; ip: string; detalle: Record<string, unknown> }
interface Cuenta { clinica: string; nombre: string; email: string; rol: string; activo: boolean }
interface Datos { dias: number; eventos: Evento[]; resumen: { alta: number; media: number; info: number }; cobertura: { cuentas: Cuenta[]; conDosPasos: number; total: number }; tipos: Record<string, string> }

const cuando = (iso: string) => new Date(iso).toLocaleString("es-EC", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const detalleTexto = (e: Evento) => Object.entries(e.detalle ?? {}).filter(([, v]) => v !== "" && v !== null).map(([k, v]) => `${k}: ${String(v).slice(0, 90)}`).join(" · ");

/** Panel Donna → «Seguridad»: eventos de seguridad recientes, quién tiene la verificación en dos pasos y herramientas para el equipo de Donna. */
export function SeguridadPanel() {
  const [dias, setDias] = useState(30);
  const [filtro, setFiltro] = useState<"todos" | "alta" | "media" | "info">("todos");
  const [d, setD] = useState<Datos | null>(null);
  const [correo, setCorreo] = useState("");
  const [msg, setMsg] = useState("");
  const a = useAccion();
  const cargar = () => api<Datos>("GET", `/api/plataforma/seguridad?dias=${dias}`).then(setD).catch(() => undefined);
  useEffect(() => { cargar(); }, [dias]);
  if (!d) return <p className="muted">Cargando…</p>;
  const lista = d.eventos.filter((e) => filtro === "todos" || e.severidad === filtro);
  const sin = d.cobertura.cuentas.filter((c) => !c.activo);

  async function resumen() {
    setMsg("");
    const r = await a.correr(() => api<{ enviado: boolean; eventos: number; motivo?: string }>("POST", "/api/plataforma/seguridad/resumen"));
    if (r) setMsg(r.enviado ? `Resumen enviado a tu correo (${r.eventos} evento${r.eventos === 1 ? "" : "s"} de los últimos 7 días).` : r.motivo ?? "No se pudo enviar.");
  }
  async function restablecer() {
    setMsg("");
    if (!confirm(`¿Restablecer la verificación en dos pasos de ${correo}? Esa persona entrará solo con su contraseña y deberá activarla otra vez. Verifica antes que de verdad es ella (por ejemplo, con una llamada).`)) return;
    const r = await a.correr(() => api<unknown>("POST", "/api/plataforma/seguridad/restablecer-2fa", { email: correo.trim() }));
    if (r !== undefined) { setMsg("Listo: la verificación quedó restablecida."); setCorreo(""); cargar(); }
  }

  return (
    <section className="card" aria-labelledby="t-seg" style={{ display: "grid", gap: 12 }}>
      <div className="fila" style={{ justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h2 id="t-seg" style={{ margin: 0 }}>Seguridad de las cuentas</h2>
        <span className={`chip${d.resumen.alta ? " rojo" : ""}`}>{d.resumen.alta ? `${d.resumen.alta} grave${d.resumen.alta === 1 ? "" : "s"} en ${d.dias} días` : `Sin eventos graves en ${d.dias} días`}</span>
      </div>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Los eventos graves te llegan al correo al instante; cada lunes llega un resumen. Aquí ves el detalle. Todo queda guardado y no se puede borrar.</p>

      <div className="fila" style={{ flexWrap: "wrap", gap: 8 }}>
        {(["todos", "alta", "media", "info"] as const).map((f) => (
          <button key={f} className="tog" aria-pressed={filtro === f} onClick={() => setFiltro(f)}>
            {f === "todos" ? `Todos (${d.eventos.length})` : f === "alta" ? `Graves (${d.resumen.alta})` : f === "media" ? `A revisar (${d.resumen.media})` : `Informativos (${d.resumen.info})`}
          </button>
        ))}
        <select className="entrada" aria-label="Periodo" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
          <option value={7}>Últimos 7 días</option><option value={30}>Últimos 30 días</option><option value={90}>Últimos 90 días</option>
        </select>
      </div>

      {lista.length === 0 ? <p className="muted" style={{ margin: 0 }}>No hay eventos en este periodo.</p> : (
        <ul className="lista-abonos">
          {lista.slice(0, 100).map((e) => (
            <li key={e.id} className="cumple">
              <div className="fila" style={{ justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                <span><b>{d.tipos[e.tipo] ?? e.tipo}</b>{e.clinica && <span className="muted"> · {e.clinica}</span>}</span>
                <span className={`chip${e.severidad === "alta" ? " rojo" : ""}`}>{e.severidad === "alta" ? "Grave" : e.severidad === "media" ? "A revisar" : "Informativo"} · {cuando(e.creado)}</span>
              </div>
              <small className="muted">{e.email}{e.ip ? ` · ${e.ip}` : ""}{detalleTexto(e) ? ` · ${detalleTexto(e)}` : ""}</small>
            </li>
          ))}
        </ul>
      )}

      <h3 style={{ margin: "6px 0 0" }}>Verificación en dos pasos ({d.cobertura.conDosPasos} de {d.cobertura.total} cuentas de administración y dirección)</h3>
      {sin.length === 0 ? <p className="muted" style={{ margin: 0 }}>Todas la tienen activa.</p> : (
        <ul className="lista-abonos">
          {sin.map((c) => <li key={c.email} className="cumple"><span><b>{c.nombre}</b> <span className="muted">· {c.clinica} · {c.email} · {c.rol === "admin" ? "administración" : "dirección"}</span></span></li>)}
        </ul>
      )}

      <h3 style={{ margin: "6px 0 0" }}>Herramientas</h3>
      <div className="fila" style={{ flexWrap: "wrap", alignItems: "flex-end", gap: 8 }}>
        <button className="btn" disabled={a.ocupado} onClick={resumen}>Enviarme el resumen ahora</button>
        <label className="campo" style={{ margin: 0 }}>Restablecer la verificación de una cuenta (perdió su celular y sus códigos)
          <input className="entrada" type="email" placeholder="correo de la cuenta" value={correo} onChange={(e) => setCorreo(e.target.value)} style={{ minWidth: 260 }} />
        </label>
        <button className="btn" disabled={a.ocupado || !correo.includes("@")} onClick={restablecer}>Restablecer</button>
      </div>
      <AvisoError error={a.error} />
      {msg && <div className="aviso ok" role="status">{msg}</div>}
    </section>
  );
}
